import {toastDefaultConfig} from '@/shared/config/toastConfig'
import {formatBytes} from '@/shared/utils'
import {useDismissImports, useImports} from '@/shared/utils/hooks/useAPIFiles'
import {useDismissOnOutside} from '@/shared/utils/hooks/useDismissOnOutside'
import React, {useCallback, useRef, useState} from 'react'
import {toast} from 'react-toastify'
import type {ImportRecord} from '../../core/importModels'
import {
  clearFinishedUploads,
  dismissUploads,
  isUploadActive,
  UploadEntry,
} from '../../core/uploadTracker'
import {
  displayDestination,
  importProgressText,
  IMPORT_STATUS_LABEL,
  isImportActive,
} from './importStatus'
import {useUploads} from './useUploads'
import './imports-dropdown.scss'

export type ImportsDropdownAppearance = 'labeled' | 'icon'

const MENU_ID = 'omics-files-imports-menu'

export interface ImportsDropdownProps {
  appearance: ImportsDropdownAppearance
  /** Opens a destination folder in Files (FileTableContext.onOpenFolder); plain text without it. */
  onOpenFolder?: (relativePath: string) => void
}

const STATUS_ICON: Record<ImportRecord['status'], string> = {
  queued: 'fas fa-clock',
  running: 'fas fa-spinner fa-spin',
  succeeded: 'fas fa-circle-check',
  partial: 'fas fa-triangle-exclamation',
  failed: 'fas fa-circle-xmark',
}

function formatTransferTime(time: string | number): string {
  const date = new Date(time)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString(undefined, {dateStyle: 'short', timeStyle: 'short'})
}

/** "to /a/b": a link that opens the folder in Files when that is possible here. */
const Destination: React.FC<{path: string; onOpenFolder?: (relativePath: string) => void}> = ({
  path,
  onOpenFolder,
}) => (
  <>
    to{' '}
    {onOpenFolder ? (
      <button
        type='button'
        className='ob-imports__folder-link'
        onClick={() => onOpenFolder(path)}
        title='Open this folder in Files'
        data-testid='omics-transfer-folder-link'
      >
        {displayDestination(path)}
      </button>
    ) : (
      displayDestination(path)
    )}
  </>
)

interface ImportItemProps {
  record: ImportRecord
  /** Hides a finished import from the list; absent while it is still going. */
  onDismiss?: (importId: string) => void
  onOpenFolder?: (relativePath: string) => void
}

const ImportItem: React.FC<ImportItemProps> = ({record, onDismiss, onOpenFolder}) => {
  const [showErrors, setShowErrors] = useState(false)
  const firstSource = record.sources[0] ?? ''
  const moreSources = record.sourceCount - 1

  return (
    <li className={`ob-imports__item ob-imports__item--${record.status}`} data-testid='omics-import-item'>
      <div className='ob-imports__item-head'>
        <i className={`${STATUS_ICON[record.status]} ob-imports__status-icon`} aria-hidden='true' />
        <span className='ob-imports__status'>{IMPORT_STATUS_LABEL[record.status]}</span>
        <time className='ob-imports__time' dateTime={record.createdAt}>
          {formatTransferTime(record.createdAt)}
        </time>
        {onDismiss && (
          <button
            type='button'
            className='ob-imports__dismiss'
            onClick={() => onDismiss(record.importId)}
            title='Remove from list'
            aria-label={`Remove ${firstSource || 'import'} from list`}
            data-testid='omics-import-dismiss'
          >
            <i className='fas fa-xmark' aria-hidden='true' />
          </button>
        )}
      </div>
      <div className='ob-imports__source' title={record.sources.join('\n')}>
        {firstSource}
        {moreSources > 0 && <span className='ob-imports__more'> +{moreSources} more</span>}
      </div>
      <div className='ob-imports__meta'>
        <Destination path={record.destinationPath} onOpenFolder={onOpenFolder} /> · {importProgressText(record)}
        {record.bytesCopied > 0 && ` · ${formatBytes(record.bytesCopied)}`}
      </div>
      {record.runner === 'fargate' && isImportActive(record.status) && (
        <div className='ob-imports__meta' data-testid='omics-import-large-runner'>
          {'Large file (>1GB) can take up to a minute to start import'}
        </div>
      )}
      {record.errors.length > 0 && (
        <>
          <button
            type='button'
            className='ob-imports__errors-toggle'
            onClick={() => setShowErrors((v) => !v)}
            aria-expanded={showErrors}
          >
            {showErrors ? 'Hide errors' : `Show errors (${record.errors.length})`}
          </button>
          {showErrors && (
            <ul className='ob-imports__errors'>
              {record.errors.map((e, i) => (
                <li key={i}>
                  {e.source && <span className='ob-imports__error-source'>{e.source}: </span>}
                  {e.error}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </li>
  )
}

const UPLOAD_STATUS_ICON: Record<UploadEntry['status'], string> = {
  uploading: 'fas fa-spinner fa-spin',
  done: 'fas fa-circle-check',
  failed: 'fas fa-circle-xmark',
}

const UPLOAD_STATUS_LABEL: Record<UploadEntry['status'], string> = {
  uploading: 'Uploading',
  done: 'Uploaded',
  failed: 'Failed',
}

/** Uploads reuse the import item styles; their status maps onto the import ones for colour. */
const UPLOAD_ITEM_MODIFIER: Record<UploadEntry['status'], string> = {
  uploading: 'running',
  done: 'succeeded',
  failed: 'failed',
}

const UploadItem: React.FC<{upload: UploadEntry; onOpenFolder?: (relativePath: string) => void}> = ({
  upload,
  onOpenFolder,
}) => {
  const percent = upload.size > 0 ? Math.min(100, Math.round((upload.loaded / upload.size) * 100)) : 100
  const active = isUploadActive(upload)
  return (
    <li className={`ob-imports__item ob-imports__item--${UPLOAD_ITEM_MODIFIER[upload.status]}`} data-testid='omics-upload-item'>
      <div className='ob-imports__item-head'>
        <i className={`${UPLOAD_STATUS_ICON[upload.status]} ob-imports__status-icon`} aria-hidden='true' />
        <span className='ob-imports__status'>{UPLOAD_STATUS_LABEL[upload.status]}</span>
        <time className='ob-imports__time' dateTime={new Date(upload.startedAt).toISOString()}>
          {formatTransferTime(upload.startedAt)}
        </time>
        {!active && (
          <button
            type='button'
            className='ob-imports__dismiss'
            onClick={() => dismissUploads([upload.id])}
            title='Remove from list'
            aria-label={`Remove ${upload.name} from list`}
            data-testid='omics-upload-dismiss'
          >
            <i className='fas fa-xmark' aria-hidden='true' />
          </button>
        )}
      </div>
      <div className='ob-imports__source' title={upload.name}>
        {upload.name}
      </div>
      {active && (
        <div
          className='ob-imports__progress'
          role='progressbar'
          aria-label={`Uploading ${upload.name}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
        >
          <div className='ob-imports__progress-bar' style={{width: `${percent}%`}} />
        </div>
      )}
      <div className='ob-imports__meta'>
        <Destination path={upload.destinationPath} onOpenFolder={onOpenFolder} /> ·{' '}
        {active ? `${formatBytes(upload.loaded)} of ${formatBytes(upload.size)}` : formatBytes(upload.size)}
      </div>
      {upload.error && <div className='ob-imports__upload-error'>{upload.error}</div>}
    </li>
  )
}

type TransferRow =
  | {kind: 'upload'; key: string; at: number; upload: UploadEntry}
  | {kind: 'import'; key: string; at: number; record: ImportRecord}

/** Uploads and imports in one list, newest first. */
function transferRows(uploads: readonly UploadEntry[], imports: ImportRecord[]): TransferRow[] {
  const rows: TransferRow[] = [
    ...uploads.map((upload) => ({kind: 'upload' as const, key: upload.id, at: upload.startedAt, upload})),
    ...imports.map((record) => ({
      kind: 'import' as const,
      key: record.importId,
      at: Date.parse(record.createdAt) || 0,
      record,
    })),
  ]
  return rows.sort((a, b) => b.at - a.at)
}

/**
 * Uploads from this tab and recent server-side imports (S3 / HTTPS / FTP) in one list, newest
 * first. Sits next to Import Data in the Files header. Uploads come from uploadTracker (this tab
 * only); imports refresh on `import.status` notifications (SocketProvider) and poll while one is
 * still active.
 */
export const ImportsDropdown: React.FC<ImportsDropdownProps> = ({appearance, onOpenFolder}) => {
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const {data, isLoading, isError, refetch} = useImports()
  const dismissMutation = useDismissImports()
  const imports = data ?? []
  const uploads = useUploads()
  const activeImports = imports.filter((i) => isImportActive(i.status)).length
  const activeUploads = uploads.filter(isUploadActive).length
  const activeCount = activeImports + activeUploads
  const finishedImports = imports.length - activeImports
  const finishedCount = finishedImports + (uploads.length - activeUploads)

  const dismiss = (request: Parameters<typeof dismissMutation.mutate>[0]) =>
    dismissMutation.mutate(request, {
      onError: () => toast.error('Could not remove imports from the list. Please try again.', toastDefaultConfig),
    })

  const close = useCallback(() => setOpen(false), [])
  const rows = transferRows(uploads, imports)

  const openFolder = onOpenFolder
    ? (relativePath: string) => {
        close()
        onOpenFolder(relativePath)
      }
    : undefined

  useDismissOnOutside(open, close, dropdownRef, triggerRef)

  const toggle = () => {
    if (!open) void refetch()
    setOpen((prev) => !prev)
  }

  const label = activeCount > 0 ? `Transfers (${activeCount} in progress)` : 'Transfers'

  const clearFinished = () => {
    clearFinishedUploads()
    if (finishedImports > 0) dismiss({allFinished: true})
  }

  const trigger = (
    <button
      ref={triggerRef}
      type='button'
      className={
        appearance === 'icon' ? `ob-files-panel__icon-btn${open ? ' active' : ''}` : 'btn btn-sm ob-imports__labeled-btn'
      }
      onClick={toggle}
      title={label}
      aria-label={label}
      aria-expanded={open}
      aria-controls={open ? MENU_ID : undefined}
      data-testid='omics-files-imports-btn'
    >
      <span className='ob-imports__trigger-icon'>
        <i className={activeCount > 0 ? 'fas fa-spinner fa-spin' : 'fas fa-list-check'} />
        {activeCount > 0 && (
          <span className='ob-imports__badge' data-testid='omics-files-imports-badge'>
            {activeCount}
          </span>
        )}
      </span>
      {appearance === 'labeled' && (
        <>
          <span className='ob-file-header__btn-label'>Transfers</span>
          <i className='fas fa-caret-down ob-imports__caret' />
        </>
      )}
    </button>
  )

  const menu = open && (
    <div
      id={MENU_ID}
      className={`ob-files-panel__dropdown-menu ob-imports__menu ob-imports__menu--${appearance}`}
      role='region'
      aria-label='Transfers'
    >
      <div className='ob-imports__title-row'>
        <span className='ob-imports__title'>Transfers</span>
        {finishedCount > 0 && (
          <button
            type='button'
            className='ob-imports__clear'
            onClick={clearFinished}
            data-testid='omics-imports-clear-finished'
          >
            Clear finished
          </button>
        )}
      </div>
      {isError && (
        <div className='ob-imports__empty'>
          {imports.length > 0 ? 'Could not refresh imports; showing the last known status.' : 'Could not load imports.'}
        </div>
      )}
      {rows.length === 0 && isLoading && <div className='ob-imports__empty'>Loading…</div>}
      {rows.length === 0 && !isLoading && !isError && (
        <div className='ob-imports__empty'>
          No transfers in the last 7 days. Use Import Data to upload files or copy them from S3, HTTPS or FTP.
        </div>
      )}
      {rows.length > 0 && (
        <ul className='ob-imports__list' data-testid='omics-transfers-list'>
          {rows.map((row) =>
            row.kind === 'upload' ? (
              <UploadItem key={row.key} upload={row.upload} onOpenFolder={openFolder} />
            ) : (
              <ImportItem
                key={row.key}
                record={row.record}
                onOpenFolder={openFolder}
                onDismiss={
                  isImportActive(row.record.status) ? undefined : (importId) => dismiss({importIds: [importId]})
                }
              />
            )
          )}
        </ul>
      )}
    </div>
  )

  // In the narrow Files panel the menu anchors to the whole header (ob-imports__dropdown--icon is
  // not positioned), so it never runs off the panel's left edge; see imports-dropdown.scss.
  const content = (
    <div className={`ob-files-panel__dropdown ob-imports__dropdown--${appearance}`} ref={dropdownRef}>
      {trigger}
      {menu}
    </div>
  )

  return appearance === 'labeled' ? <li style={{margin: 0, position: 'relative'}}>{content}</li> : content
}
